import { Box, Flex, useTheme } from '@chakra-ui/react';
import PropTypes from 'prop-types';
import { CloseNarrowIcon, PlusIcon } from '~/components/icons';
import { hexToRgbaString } from '~/utils';

// eslint-disable-next-line react/prop-types
const Wrapper = ({ children, isDisabled, ...props }) => (
  <Box
    _focus={{
      borderColor: 'primary.500',
      boxShadow: 'outline',
      outline: 'unset',
    }}
    as="button"
    disabled={isDisabled}
    listStyleType="none"
    position="relative"
    userSelect="none"
    {...props}
  >
    {children}
  </Box>
);

// eslint-disable-next-line react/prop-types
const Overlay = ({ isSelected, isDisabled, children, ...props }) => {
  const theme = useTheme();
  return (
    <Flex
      _hover={{
        backgroundColor: isDisabled
          ? 'unset'
          : hexToRgbaString(theme.colors.black, 0.4),
      }}
      alignItems="center"
      backgroundColor={isSelected && hexToRgbaString(theme.colors.black, 0.4)}
      cursor={isDisabled ? 'not-allowed' : 'pointer'}
      height="100%"
      justifyContent="center"
      position="absolute"
      width="100%"
      zIndex="2"
      {...props}
    >
      {children}
    </Flex>
  );
};

// eslint-disable-next-line react/prop-types
const IconWrapper = ({ children, ...props }) => (
  <Flex
    _groupActive={{
      transform: 'scale3d(1.2,1.2,1.2)',
    }}
    alignItems="center"
    backgroundColor="white"
    height="30px"
    justifyContent="center"
    rounded="full"
    transition="transform .1s cubic-bezier(0.24, 1.35, 0.73, 1.7)"
    width="30px"
    {...props}
  >
    {children}
  </Flex>
);

const propTypes = {
  children: PropTypes.node.isRequired,
  hasReachedMax: PropTypes.bool,
  id: PropTypes.string.isRequired,
  isDisabled: PropTypes.bool,
  isSelected: PropTypes.bool,
  onClick: PropTypes.func,
};

export const Item = ({
  children,
  hasReachedMax = false,
  id,
  isDisabled = false,
  isSelected = false,
  onClick,
  ...props
}) => {
  let content;
  if (isSelected) {
    content = (
      <IconWrapper>
        <CloseNarrowIcon color="primary.500" />
      </IconWrapper>
    );
  } else if (!hasReachedMax) {
    content = (
      <IconWrapper
        _groupHover={{ display: isDisabled ? 'none' : 'flex' }}
        display="none"
        backgroundColor="secondary.500"
      >
        <PlusIcon color="white" />
      </IconWrapper>
    );
  }

  return (
    <Wrapper
      aria-checked={isSelected}
      isDisabled={isDisabled}
      onClick={() => onClick(id)}
      role="checkbox"
      {...props}
    >
      <Overlay role="group" isDisabled={isDisabled} isSelected={isSelected}>
        {content}
      </Overlay>
      {children}
    </Wrapper>
  );
};

Item.propTypes = propTypes;
